import React from "react";
import { get } from "lodash";
import { Col } from "react-bootstrap";
import { AuditFields, EditButton } from "MODULES/shared/components";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Map, fromJS } from "immutable";
import loadRecord from "MODULES/shared/decorators/loadRecord";
import {
  ACCOUNTING,
  ADMIN,
  QUALITY_CONTROL,
  USER,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  WAREHOUSE_MANAGER
} from "MODULES/users/constants";
import Attachments from "MODULES/shared/components/Attachments/Attachments";
import Notes from "MODULES/shared/components/Notes/Notes";
import resourceTypes from "MODULES/shared/types/resourceTypes";
import Contact from "MODULES/Orders/shared/Contact";
import { PURCHASE_FOR_EXCHANGE } from "MODULES/RepairOrders/constants";
import { TraceHoldDetail } from "MODULES/PurchaseOrders/components/Detail/TraceHoldDetails";
import Billing from "MODULES/PurchaseOrders/components/Detail/Billing/Billing";
import DashedSeparator from "MODULES/shared/components/DashedSeparator/DashedSeparator";
import ReceivingRecordsTable from "MODULES/PurchaseOrders/components/Detail/ReceivingRecordsTable/ReceivingRecordsTable";
import { hasAnyOfRolesOrSubroles, hasSubRole } from "MODULES/users/helpers";
import Note from "MODULES/shared/components/Note";
import CoreReceivedTable from "MODULES/PurchaseOrders/components/Detail/CoreReceivedTable/CoreReceivedTable";
import { VendorDetail } from "MODULES/shared/components/VendorDetail/VendorDetail";
import { ApprovalDetails } from "MODULES/shared/components/ApprovalDetails/ApprovalDetails";
import { properCase } from "LIB/formatters/text";
import { show, update } from "../../actions";
import { PhysicalAddress } from "./PhysicalAddress";
import { ShippingAddress } from "./ShippingAddress";
import { ShippingPreference } from "../../../Orders/shared/ShippingPreference";
import ShippingDetails from "./ShippingDetails";
import EditPurchaseOrder from "../Edit/EditPurchaseOrder";
import Header from "./Header";
import LineItemsTable from "./LineItemsTable/LineItemsTable";
import AddPart from "./LineItemsTable/AddPart/AddPart";
import { RejectionDetails } from "./RejectionDetails";
import {
  ATTACHMENT_TYPES,
  EXTERNAL_NOTE,
  INTERNAL_NOTE
} from "../../constants";

export class Detail extends React.Component {
  static propTypes = {
    purchaseOrder: PropTypes.instanceOf(Map),
    currentUser: PropTypes.instanceOf(Object),
    location: PropTypes.shape({
      state: PropTypes.shape({
        part: PropTypes.instanceOf(Object),
        open: PropTypes.bool,
        id: PropTypes.number
      })
    })
  };

  state = {
    editPOModalOpen: false,
    addPartModalOpen: false,
    editInvoiceModalOpen: false,
    editPartModalOpen: false,
    initialPartFormValues: {}
  };

  componentWillMount() {
    if (this.props.location.state && this.props.location.state.open) {
      this.setState({
        addPartModalOpen: true,
        initialPartFormValues: this.props.location.state.part,
        id: this.props.location.state.id
      });
    }
  }

  openEditPOModal = () => {
    this.setState({ editPOModalOpen: true });
  };

  openEditInvoiceModal = () => {
    this.setState({ editInvoiceModalOpen: true });
  };

  openAddPartModal = () => this.setState({ addPartModalOpen: true });

  closeAddPartModal = () =>
    this.setState({ addPartModalOpen: false, initialPartFormValues: null });

  closeEditPOModal = () => this.setState({ editPOModalOpen: false });

  closeEditInvoiceModal = () => this.setState({ editInvoiceModalOpen: false });

  purchaseOrderReceived() {
    const purchaseOrder = this.props.purchaseOrder.toJS();

    return (
      !!purchaseOrder.line_items.length &&
      purchaseOrder.line_items.every(
        item => item.quantity === item.quantity_received
      )
    );
  }

  purchaseOrderWithOneOrMoreLineItemsPaid() {
    const purchaseOrder = this.props.purchaseOrder.toJS();

    return (
      !!purchaseOrder.line_items.length &&
      purchaseOrder.line_items.some(
        item =>
          ![null, undefined].includes(item.amount_paid) &&
          item.amount_paid >= item.cost * item.quantity
      )
    );
  }

  userCanEdit() {
    const { currentUser } = this.props;
    const purchaseOrder = this.props.purchaseOrder.toJS();
    const {
      buyer_id: buyerId,
      co_buyer_id: coBuyerId,
      third_buyer_id: thirdBuyerId
    } = purchaseOrder;

    return (
      hasAnyOfRolesOrSubroles(
        currentUser,
        [ACCOUNTING, QUALITY_CONTROL],
        [WAREHOUSE_LOGISTICS_DIRECTOR]
      ) ||
      currentUser.id === buyerId ||
      currentUser.id === coBuyerId ||
      currentUser.id === thirdBuyerId
    );
  }

  inventoryLocked() {
    const purchaseOrder = this.props.purchaseOrder.toJS();

    const paid = this.purchaseOrderWithOneOrMoreLineItemsPaid();
    const userCanEdit = this.userCanEdit();

    return !userCanEdit || paid || purchaseOrder.cancelled;
  }

  shippingDetailsUnlocked() {
    const { currentUser } = this.props;

    const userCanEdit = this.userCanEdit();
    const isWarehouseManager = hasSubRole(currentUser, WAREHOUSE_MANAGER);
    return userCanEdit || isWarehouseManager;
  }

  render() {
    const {
      editPOModalOpen,
      addPartModalOpen,
      initialPartFormValues,
      id
    } = this.state;
    const purchaseOrder = this.props.purchaseOrder.toJS();
    const lineItems = purchaseOrder.line_items;
    const locked = this.inventoryLocked();
    const shippingDetailsUnlocked = this.shippingDetailsUnlocked();
    const received = this.purchaseOrderReceived();

    return (
      <div>
        <Header purchaseOrder={fromJS(purchaseOrder)} received={received} />
        <div className="row">
          <Col md={3}>
            <div className="panel panel-default">
              <div className="panel-body">
                <h2 style={{ marginTop: 10 }}>
                  Details
                  {!locked && <EditButton handleClick={this.openEditPOModal} />}
                  <EditPurchaseOrder
                    purchaseOrder={purchaseOrder}
                    open={editPOModalOpen}
                    close={this.closeEditPOModal}
                  />
                </h2>
                <VendorDetail record={purchaseOrder} />
                <DashedSeparator />
                <PhysicalAddress
                  address={purchaseOrder.company.billing_address}
                />
                {!purchaseOrder.portal_purchase && [
                  <DashedSeparator />,
                  <Contact contact={fromJS(purchaseOrder.contact)} />
                ]}
                <ApprovalDetails record={purchaseOrder} />
                <RejectionDetails purchaseOrder={purchaseOrder} />
                <TraceHoldDetail purchaseOrder={purchaseOrder} />
                {get(
                  purchaseOrder,
                  ["line_items[0]", PURCHASE_FOR_EXCHANGE].join(".")
                ) && [<DashedSeparator />, <Billing />]}
                <DashedSeparator />
                <ShippingDetails
                  purchaseOrder={purchaseOrder}
                  unlocked={shippingDetailsUnlocked}
                />
                <DashedSeparator />
                {purchaseOrder.ship_to_address && (
                  <ShippingAddress purchaseOrder={purchaseOrder} />
                )}
                <DashedSeparator />
                {purchaseOrder.shipping_method && (
                  <ShippingPreference
                    shippingMethod={purchaseOrder.shipping_method}
                    amountInsured={purchaseOrder.amt_insured}
                    pctInsured={purchaseOrder.pct_insured}
                  />
                )}
                <DashedSeparator />
                <AuditFields record={fromJS(purchaseOrder)} multiLine />
              </div>
            </div>
          </Col>
          <Col md={9}>
            <h2 style={{ marginTop: 26 }}>
              Parts
              {(!locked || this.props.currentUser.role === ADMIN) && (
                <button
                  type="button"
                  className="btn btn-primary btn-hollow pull-right"
                  onClick={this.openAddPartModal}
                >
                  <i className="fa fa-fw fa-plus">&nbsp;</i>
                  Add Part
                </button>
              )}
              <AddPart
                open={addPartModalOpen}
                close={this.closeAddPartModal}
                paid={this.purchaseOrderWithOneOrMoreLineItemsPaid()}
                part={initialPartFormValues}
                id={id}
              />
            </h2>
            <LineItemsTable results={lineItems} />
            <div className="row">
              <Col sm={12}>
                <h2 style={{ marginTop: 26 }}>Received</h2>
                <ReceivingRecordsTable />
              </Col>
            </div>
            {lineItems.length > 0 &&
              lineItems.some(f => f.purchase_for_exchange) && (
                <div className="row">
                  <Col sm={12}>
                    <h2 style={{ marginTop: 26 }}>Cores Assigned To Return</h2>
                    <CoreReceivedTable purchaseOrderId={purchaseOrder.id} />
                  </Col>
                </div>
              )}
            <div className="row">
              <Col sm={6}>
                <Note
                  header={properCase(INTERNAL_NOTE)}
                  type={INTERNAL_NOTE}
                  record={purchaseOrder}
                  update={update}
                />
              </Col>
              <Col sm={6}>
                <Note
                  header={properCase(EXTERNAL_NOTE)}
                  record={purchaseOrder}
                  type={EXTERNAL_NOTE}
                  update={update}
                />
              </Col>
            </div>
            <Notes
              headerLabel="Follow Up Notes"
              resource={fromJS(this.props.purchaseOrder)}
              resourceType={resourceTypes.purchaseOrder}
              resourceKey="purchaseOrder"
            />
            <Notes
              resource={fromJS(this.props.purchaseOrder)}
              resourceType={resourceTypes.purchaseOrder}
              resourceKey="purchaseOrder"
              trace
            />
            <hr className="hr-dashed" />
            <Attachments
              attachmentTypes={ATTACHMENT_TYPES}
              multiple
              resource={fromJS(purchaseOrder)}
              resourceType={resourceTypes.purchaseOrder}
              resourceKey="purchaseOrder"
            />
          </Col>
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  purchaseOrder: state.purchaseOrderDetail.get("purchaseOrder"),
  currentUser: state.session.get(USER).toJS()
}))(
  loadRecord({
    reducer: "purchaseOrderDetail",
    name: "purchaseOrder",
    action: show
  })(Detail)
);



// WEBPACK FOOTER //
// ./app/modules/PurchaseOrders/components/Detail/Detail.jsx